import React from 'react';
import { FormattedNumber } from 'react-intl';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { ramsAOptions, ramsEcOptions, ramsEnvOptions, ramsROptions, ramsSOptions } from '../../../utils/data/rams';
import * as htmlHelper from '../../../utils/htmlHelper';
import { PanelButtonLink, PanelHeading, PanelResult, PanelResultHeader, PanelRouterLink, TableExpandButton } from '../../elements';
import { IMaintenanceMeasure } from '../../interfaces/IMaintenanceMeasure';
import { Spinner } from '../../spinners';
import { ConditionDot } from '../../utils/ConditionDot';
import { ConditionScoreLabel } from '../../utils/ConditionScoreLabel';
import { InvestigationPriorityLabel } from '../../utils/InvestigationPriorityLabel';
import { RamsWeightedPrioLabel } from '../../utils/RamsWeightedPrioLabel';
import { UnitOfMeasurement } from '../../utils/UnitOfMeasurement';
import { AssetsViewer } from '../Common/AssetsViewer';
import { RamsItem } from '../Common/RamsItem';
import { IElement } from '../Interfaces/IElement';
import { IManifestation } from '../Interfaces/IManifestation';
import { IUnit } from '../Interfaces/IUnit';
import { ProjectMeasurements } from '../UTMeasurement/ProjectMeasurements';

interface IConditionReportNenState {
    isShowEmptyItems: boolean;
    isShowEmptyItemButton?: boolean;
}

interface IConditionReportNenProps extends ITranslateProps {
    breakDownOpenState: { [key: string]: boolean };
    constructionYear?: undefined | number;
    elements: IElement[];
    isAssetDescriptionVisible: boolean;
    isCareScoreAvailable?: undefined | boolean;
    isCompareAvailable?: undefined | boolean;
    isLoadingElements: boolean;
    isNen: boolean;
    isCompareView?: undefined | boolean;
    isShowEmptyItems?: undefined | boolean;
    maintenanceMeasures: IMaintenanceMeasure[];
    surveyId?: undefined | string;
    title?: undefined | string;
    onChangeBreakdownOpenState: (id: string) => void;
    onCollapseAll: () => void;
    onExpandAll: () => void;
    onExpandDecomposition: () => void;
}

class ConditionReportNenComponent extends React.Component<IConditionReportNenProps, IConditionReportNenState> {
    constructor(props: IConditionReportNenProps) {
        super(props);

        this.state = {
            isShowEmptyItems: false,
            isShowEmptyItemButton: false,
        };
    }

    public override componentDidUpdate(prevProps: IConditionReportNenProps) {
        if (prevProps.elements !== this.props.elements) {
            this.setState({ isShowEmptyItemButton: this.hasHiddenElements() });
        }
    }

    public override render() {
        return (
            <div className="panel" id="ConditionAssessment">
                <PanelHeading text={this.props.title ? this.props.title : this.props.t('survey.conditionReport')} icon="fa fa-tasks">
                    {this.props.isCompareAvailable && (
                        <PanelRouterLink
                            to={`/surveys/compare/${this.props.surveyId}/`}
                            text={this.props.t('survey.compare')}
                            icon="fa fa-exchange"
                            small={true}
                        />
                    )}
                    {this.props.isCompareView && this.props.surveyId && (
                        <PanelRouterLink
                            to={`/surveys/${this.props.surveyId}/`}
                            text={this.props.t('survey.showReport')}
                            icon="fa fa-search"
                            small={true}
                        />
                    )}
                </PanelHeading>
                <div className="panel-body" id="reportContent">
                    <PanelButtonLink
                        onClick={() => this.props.onExpandAll()}
                        text={this.props.t('survey.expandAll')}
                        icon="fa fa-plus-square-o"
                        xsmall={true}
                        rightAllign={true}
                    />
                    <PanelButtonLink
                        onClick={() => this.props.onCollapseAll()}
                        text={this.props.t('survey.collapseAll')}
                        icon="fa fa-minus-square-o "
                        xsmall={true}
                        rightAllign={true}
                    />
                    <PanelButtonLink
                        onClick={() => this.props.onExpandDecomposition()}
                        text={this.props.t('survey.expandAllComposition')}
                        icon="fa fa-plus-square-o"
                        xsmall={true}
                        rightAllign={true}
                    />
                    {this.state.isShowEmptyItemButton && !this.props.isCompareView && (
                        <PanelButtonLink
                            onClick={() => this.setState({ isShowEmptyItems: !this.state.isShowEmptyItems })}
                            text={
                                this.state.isShowEmptyItems ? this.props.t('survey.defectElementsVisible') : this.props.t('survey.allElementsVisible')
                            }
                            icon="fa fa-check-square-o "
                            xsmall={true}
                            rightAllign={true}
                        />
                    )}
                    {this.props.isLoadingElements && (
                        <table className="table">
                            <thead key="spinner">
                                <tr>
                                    <th>
                                        <Spinner />
                                    </th>
                                </tr>
                            </thead>
                        </table>
                    )}
                    <div id="printview">
                        {this.props.elements.length > 0 && (
                            <table className="table">
                                <thead className="mt10">
                                    <tr className="dark">
                                        <th>{this.props.t('common.code')}</th>
                                        <th>{this.props.t('common.part')}</th>
                                        <th>{this.props.t('common.material')}</th>
                                        <th className="text-right">{this.props.t('common.condition')}</th>
                                        {this.props.isCareScoreAvailable && <th className="text-right">{this.props.t('common.care')}</th>}
                                        <th>&nbsp; </th>
                                    </tr>
                                </thead>
                                {this.props.elements.map(
                                    (element) =>
                                        this.shouldShowElement(element) && (
                                            <React.Fragment key={element.id}>
                                                <thead className="object-table-element" key={element.id}>
                                                    <tr className="primary">
                                                        <th style={{ whiteSpace: 'nowrap' }}>{element.code}</th>
                                                        <th>{element.name}</th>
                                                        <th>&nbsp;</th>
                                                        <th className="text-right">
                                                            <ConditionScoreLabel value={this.getElementConditionScore(element)} />
                                                        </th>
                                                        {this.props.isCareScoreAvailable && (
                                                            <th className="text-right">
                                                                <ConditionScoreLabel value={this.getElementCareScore(element)} />
                                                            </th>
                                                        )}
                                                        <th className="text-right" style={{ height: '50px' }}>
                                                            {(element.conditionId ||
                                                                (element.defects && element.defects.length > 0) ||
                                                                (element.assets && element.assets.length > 0) ||
                                                                (element.utProjects && element.utProjects.length > 0) ||
                                                                (element.units && element.units.length > 0)) && (
                                                                <TableExpandButton
                                                                    open={this.props.breakDownOpenState[element.id]}
                                                                    onClick={() => this.props.onChangeBreakdownOpenState(element.id)}
                                                                />
                                                            )}
                                                        </th>
                                                    </tr>
                                                    {this.renderConditionDetails(element, false, element.units && element.units.length > 0)}
                                                </thead>
                                                <tbody
                                                    className={this.props.breakDownOpenState[element.id] ? 'object-table-unit' : 'hidden'}
                                                    key={`${element.id}tbody`}
                                                >
                                                    {element.units.map(
                                                        (unit) =>
                                                            this.shouldShowUnit(unit) && (
                                                                <React.Fragment key={unit.id}>
                                                                    <tr key={unit.id} className={'bg-light darker'}>
                                                                        <td style={{ whiteSpace: 'nowrap' }}>{unit.code}</td>
                                                                        <td>{unit.name}</td>
                                                                        <td>
                                                                            {unit.material}&nbsp;
                                                                            {unit.quantity}
                                                                            <UnitOfMeasurement value={unit.quantityUnitOfMeasurement} />
                                                                        </td>
                                                                        <td className="text-right">
                                                                            <ConditionScoreLabel value={this.getUnitConditionScore(unit)} />
                                                                        </td>
                                                                        {this.props.isCareScoreAvailable && (
                                                                            <td className="text-right">
                                                                                <ConditionScoreLabel value={this.getUnitCareScore(unit)} />
                                                                            </td>
                                                                        )}
                                                                        <td className="text-right" style={{ height: '50px' }}>
                                                                            {(unit.conditionAdvice ||
                                                                                unit.conditionCost ||
                                                                                unit.investigationPriority ||
                                                                                (unit.defects && unit.defects.length > 0) ||
                                                                                (unit.assets && unit.assets.length > 0) ||
                                                                                (unit.utProjects && unit.utProjects.length > 0) ||
                                                                                (unit.manifestations && unit.manifestations.length > 0) ||
                                                                                (unit.maintenanceMeasures &&
                                                                                    unit.maintenanceMeasures.length > 0)) && (
                                                                                <TableExpandButton
                                                                                    open={this.props.breakDownOpenState[unit.id]}
                                                                                    onClick={() => this.props.onChangeBreakdownOpenState(unit.id)}
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                    {this.renderConditionDetails(unit)}
                                                                    {unit.manifestations.map((manifestation) => (
                                                                        <React.Fragment key={manifestation.id}>
                                                                            <tr
                                                                                key={manifestation.id}
                                                                                className={this.props.breakDownOpenState[unit.id] ? '' : 'hidden'}
                                                                            >
                                                                                <td style={{ whiteSpace: 'nowrap' }}>{manifestation.code}</td>
                                                                                <td>{manifestation.name}</td>
                                                                                <td>&nbsp;</td>
                                                                                <td className="text-right">
                                                                                    <ConditionScoreLabel value={manifestation.score} />
                                                                                </td>
                                                                                {this.props.isCareScoreAvailable && (
                                                                                    <td className="text-right">
                                                                                        <ConditionScoreLabel value={manifestation.careScore} />
                                                                                    </td>
                                                                                )}
                                                                                <td className="text-right" style={{ height: '50px' }}>
                                                                                    {(manifestation.conditionAdvice ||
                                                                                        manifestation.conditionCost ||
                                                                                        manifestation.investigationPriority ||
                                                                                        (manifestation.defects && manifestation.defects.length > 0) ||
                                                                                        (manifestation.assets && manifestation.assets.length > 0) ||
                                                                                        (manifestation.utProjects &&
                                                                                            manifestation.utProjects.length > 0) ||
                                                                                        (unit.maintenanceMeasures &&
                                                                                            unit.maintenanceMeasures.length > 0)) && (
                                                                                        <TableExpandButton
                                                                                            open={this.props.breakDownOpenState[manifestation.id]}
                                                                                            onClick={() =>
                                                                                                this.props.onChangeBreakdownOpenState(
                                                                                                    manifestation.id,
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    )}
                                                                                </td>
                                                                            </tr>

                                                                            {this.renderConditionDetails(
                                                                                manifestation,
                                                                                !this.props.breakDownOpenState[unit.id],
                                                                            )}
                                                                        </React.Fragment>
                                                                    ))}
                                                                </React.Fragment>
                                                            ),
                                                    )}
                                                </tbody>
                                            </React.Fragment>
                                        ),
                                )}
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private renderConditionDetails(item: IElement | IUnit | IManifestation, isParentHidden?: boolean, isConditionDefectHidden?: boolean) {
        if (
            !(
                item.conditionCost ||
                item.conditionAdvice ||
                item.investigationPriority ||
                (item.defects && item.defects.length > 0) ||
                (item.assets && item.assets.length > 0) ||
                (item.utProjects && item.utProjects.length > 0) ||
                (item.maintenanceMeasures && item.maintenanceMeasures.length > 0)
            )
        ) {
            return null;
        }
        const selectedMaintenanceMeasures = this.props.maintenanceMeasures.filter(
            (measure: IMaintenanceMeasure) => measure.unit?.id === item.id || measure.manifestation?.id === item.id,
        );

        return (
            <tr className={this.props.breakDownOpenState[item.id] && !isParentHidden ? 'object-table-condition' : 'hidden'} key={`${item.id}cond`}>
                <td style={{ border: 'none' }}>&nbsp;</td>
                <td colSpan={4} style={{ borderLeft: '1px solid #f4f4f4' }}>
                    <div className="dd-content">
                        {/* Assets */}

                        {item.assets && item.assets.length > 0 && (
                            <PanelResult>
                                <AssetsViewer assets={item.assets} surveyId={this.props.surveyId} />
                            </PanelResult>
                        )}

                        {/* UtProjects */}
                        {item.utProjects && item.utProjects.length > 0 && (
                            <>
                                {item.utProjects &&
                                    item.utProjects.map((utProject, i) => (
                                        <PanelResult key={i}>
                                            <div key={utProject.id}>
                                                <ProjectMeasurements project={utProject} constructionYear={this.props.constructionYear} />
                                            </div>
                                            {utProject.assets && <AssetsViewer assets={utProject.assets} surveyId={this.props.surveyId} />}
                                        </PanelResult>
                                    ))}
                            </>
                        )}

                        {/* Defects */}
                        {item.defects && item.defects.length > 0 && !isConditionDefectHidden && (
                            <>
                                <PanelResultHeader>{this.props.t('common.defects')}</PanelResultHeader>
                                <PanelResult>
                                    <div>
                                        {item.defects.map((defect, i) => (
                                            <div className={i === 0 ? 'mb10' : 'mb10 mt30'} key={defect.id}>
                                                <span className="fs14 fw600">{`${(i + 1).toString()}. ${defect.name}`}</span>{' '}
                                                {defect.code && <span>{defect.code}</span>}
                                                <p>{defect.description}</p>
                                                <table className="table table-condensed mw600 mb10">
                                                    <tbody key={`${defect.id}tbody`}>
                                                        <tr className="bg-light darker">
                                                            <th>{this.props.t('defect.seriousness')}</th>
                                                            <th>{this.props.t('defect.intensity')}</th>
                                                            <th>{this.props.t('defect.extend')}</th>
                                                            <th>
                                                                {(this.props.isNen && defect.defectType === 'Tekortkoming' && (
                                                                    <>{this.props.t('common.care')}</>
                                                                )) || <>{this.props.t('common.condition')}</>}
                                                            </th>
                                                        </tr>
                                                        <tr>
                                                            <td>{defect.seriousness && this.props.t(`seriousness.level${defect.seriousness}`)} </td>
                                                            <td>{defect.intensity && this.props.t(`intensity.level${defect.intensity}`)} </td>
                                                            <td>{defect.extend && this.props.t(`extend.level${defect.extend}`)}</td>
                                                            <td>
                                                                {(this.props.isNen && defect.defectType === 'Tekortkoming' && (
                                                                    <ConditionDot value={defect.careScore} />
                                                                )) || <ConditionDot value={defect.score} />}
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                                {this.props.isNen && (
                                                    <>
                                                        {defect.aspect && (
                                                            <>
                                                                {' '}
                                                                <span className="fw600">Aspect: </span>{' '}
                                                                <span className="mr40">{defect.aspect} </span>{' '}
                                                            </>
                                                        )}
                                                        {defect.cause && (
                                                            <>
                                                                {' '}
                                                                <span className="fw600">{this.props.t('damage.cause')}: </span>{' '}
                                                                <span className="mr40">{defect.cause} </span>{' '}
                                                            </>
                                                        )}
                                                        {defect.riscLevel && (
                                                            <>
                                                                {' '}
                                                                <span className="fw600">Risico niveau: </span>{' '}
                                                                <span className="mr40">{defect.riscLevel} </span>{' '}
                                                            </>
                                                        )}

                                                        {(defect.ramsR || defect.ramsA || defect.ramsS || defect.ramsEc || defect.ramsEnv) && (
                                                            <div className="row mt10">
                                                                <div className="col-md-5">
                                                                    <RamsItem
                                                                        topLetter="R"
                                                                        ramsScore={defect.ramsR}
                                                                        ramsOptions={ramsROptions}
                                                                        header={this.props.t('rams.reliability')}
                                                                    />
                                                                    <RamsItem
                                                                        topLetter="A"
                                                                        ramsScore={defect.ramsA}
                                                                        ramsOptions={ramsAOptions}
                                                                        header={this.props.t('rams.availability')}
                                                                    />
                                                                    <RamsItem
                                                                        topLetter="S"
                                                                        ramsScore={defect.ramsS}
                                                                        ramsOptions={ramsSOptions}
                                                                        header={this.props.t('rams.safety')}
                                                                    />
                                                                    <RamsItem
                                                                        topLetter="E"
                                                                        ramsScore={defect.ramsEc}
                                                                        ramsOptions={ramsEcOptions}
                                                                        header={this.props.t('rams.consequentialCosts')}
                                                                    />
                                                                    <RamsItem
                                                                        topLetter="E"
                                                                        ramsScore={defect.ramsEnv}
                                                                        ramsOptions={ramsEnvOptions}
                                                                        header={this.props.t('rams.environment')}
                                                                    />
                                                                </div>
                                                                {defect.ramsWeightedPriority && (
                                                                    <div className="col-md-3">
                                                                        <div className="mt30">
                                                                            <RamsWeightedPrioLabel value={defect.ramsWeightedPriority} />
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                                <div className="mt10">
                                                    {this.props.isNen && defect.details && (
                                                        <p>
                                                            <strong>{this.props.t('damage.details')}: </strong>
                                                            {defect.details}
                                                        </p>
                                                    )}
                                                    {this.props.isNen && defect.locationDescription && (
                                                        <p>
                                                            <strong>{this.props.t('damage.location')}: </strong>
                                                            {defect.locationDescription}
                                                        </p>
                                                    )}
                                                    {this.props.isNen && defect.repairAdvice && (
                                                        <p>
                                                            <strong>{this.props.t('damage.advice')}: </strong>
                                                            {defect.repairAdvice}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </PanelResult>
                            </>
                        )}

                        {/* Condition */}
                        {(item.conditionAdvice || item.conditionCost || item.investigationPriority) && !isConditionDefectHidden && (
                            <>
                                <PanelResultHeader>{this.props.t('survey.conditionAndAdvice')}</PanelResultHeader>
                                <PanelResult>
                                    <div>
                                        {htmlHelper.newLineToBreak(item.conditionAdvice)}
                                        <p>
                                            <span>
                                                {item.conditionCost ? `${this.props.t('survey.costEstimationPrefix') + item.conditionCost}. ` : ''}
                                                <span>
                                                    {item.conditionCostYear
                                                        ? this.props.t('survey.costEstimationYearPrefix') + item.conditionCostYear
                                                        : ''}
                                                </span>
                                            </span>
                                        </p>
                                        {item.investigationPriority && (
                                            <div>
                                                {this.props.t('survey.investigationPriority')}:&nbsp;&nbsp;
                                                <InvestigationPriorityLabel value={item.investigationPriority} />
                                            </div>
                                        )}
                                    </div>
                                </PanelResult>
                            </>
                        )}

                        {/* Maintenance */}
                        {selectedMaintenanceMeasures.length > 0 && (
                            <>
                                <PanelResultHeader>{this.props.t('maintenanceMeasure.maintenanceMeasures')}</PanelResultHeader>
                                <PanelResult>
                                    <div>
                                        <table className="table table-condensed mb10">
                                            <tbody>
                                                <tr className="bg-light darker">
                                                    <th>{this.props.t('maintenanceMeasure.maintenanceMeasure')}</th>
                                                    <th>{this.props.t('common.cost')}</th>
                                                    <th>{this.props.t('maintenanceMeasure.cycle')}</th>
                                                    <th>{this.props.t('maintenanceMeasure.planned')}</th>
                                                    <th>{this.props.t('maintenanceMeasure.completed')}</th>
                                                </tr>
                                                {selectedMaintenanceMeasures.map((maintenance, i) => (
                                                    <tr key={i}>
                                                        <td>
                                                            {maintenance.description}
                                                            {(maintenance.maintenanceType && (
                                                                <div className="fs12 text-muted-darker">
                                                                    {maintenance.maintenanceType} {maintenance.workType}
                                                                </div>
                                                            )) || (
                                                                <div className="fs12 text-muted-darker">
                                                                    {this.props.t('maintenanceMeasure.correctiveMaintenance')}{' '}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {' '}
                                                            <FormattedNumber value={maintenance.cost ?? 0} style="currency" currency="EUR" />
                                                        </td>
                                                        <td>{maintenance.cycle}</td>
                                                        <td>
                                                            {maintenance.planYear}{' '}
                                                            {maintenance.finalPlanYear && <span>-{maintenance.finalPlanYear}</span>}
                                                        </td>
                                                        <td>{maintenance.implementationYear}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </PanelResult>
                            </>
                        )}
                    </div>
                </td>
            </tr>
        );
    }

    private hasHiddenElements() {
        for (const element of this.props.elements) {
            if (!this.shouldShowElement(element)) {
                return true;
            }
        }
        return false;
    }

    private shouldShowElement(element: IElement) {
        if ((this.state.isShowEmptyItems || this.props.isCompareView) && !element.isArchived) {
            return true;
        }
        if (this.getElementConditionScore(element)) {
            return true;
        }
        if (this.getElementCareScore(element)) {
            return true;
        }
        if (
            element.conditionId ||
            (element.defects && element.defects.length > 0) ||
            (element.assets && element.assets.length > 0) ||
            (element.utProjects && element.utProjects.length > 0)
        ) {
            return true;
        }
        for (const unit of element.units) {
            if (
                unit.conditionAdvice ||
                unit.conditionCost ||
                unit.investigationPriority ||
                (unit.defects && unit.defects.length > 0) ||
                (unit.assets && unit.assets.length > 0) ||
                (unit.utProjects && unit.utProjects.length > 0) ||
                (unit.maintenanceMeasures && unit.maintenanceMeasures.length > 0)
            ) {
                return true;
            }
            for (const manifestation of unit.manifestations) {
                if (
                    manifestation.conditionAdvice ||
                    manifestation.conditionCost ||
                    manifestation.investigationPriority ||
                    (manifestation.defects && manifestation.defects.length > 0) ||
                    (manifestation.assets && manifestation.assets.length > 0) ||
                    (manifestation.utProjects && manifestation.utProjects.length > 0) ||
                    (unit.maintenanceMeasures && unit.maintenanceMeasures.length > 0)
                ) {
                    return true;
                }
            }
        }
        return false;
    }

    private shouldShowUnit(unit: IUnit) {
        if ((this.state.isShowEmptyItems || this.props.isCompareView) && !unit.isArchived) {
            return true;
        }
        if (this.getUnitCareScore(unit)) {
            return true;
        }
        if (this.getUnitConditionScore(unit)) {
            return true;
        }
        if (
            unit.conditionAdvice ||
            unit.conditionCost ||
            unit.investigationPriority ||
            (unit.defects && unit.defects.length > 0) ||
            (unit.assets && unit.assets.length > 0) ||
            (unit.utProjects && unit.utProjects.length > 0) ||
            (unit.maintenanceMeasures && unit.maintenanceMeasures.length > 0)
        ) {
            return true;
        }
        for (const manifestation of unit.manifestations) {
            if (
                manifestation.conditionAdvice ||
                manifestation.conditionCost ||
                manifestation.investigationPriority ||
                (manifestation.defects && manifestation.defects.length > 0) ||
                (manifestation.assets && manifestation.assets.length > 0) ||
                (manifestation.utProjects && manifestation.utProjects.length > 0) ||
                (unit.maintenanceMeasures && unit.maintenanceMeasures.length > 0)
            ) {
                return true;
            }
        }
        return false;
    }

    private getConditionScoreFromChildren(items: IUnit[] | IManifestation[], useCareScore: boolean) {
        if (items.length === 0) {
            return undefined;
        }

        let scores = [] as string[];
        for (const item of items) {
            if (item.conditionId) {
                if (useCareScore) {
                    scores.push(item.careScore ?? '');
                } else {
                    scores.push(item.score ?? '');
                }
            } else {
                if ((item as IUnit).manifestations) {
                    const childScore = this.getConditionScoreFromChildren((item as IUnit).manifestations, useCareScore);
                    if (childScore) {
                        scores.push(childScore);
                    }
                }
            }
        }
        if (scores.length === 0) {
            return undefined;
        }
        scores = scores.sort().reverse();
        return scores[0];
    }

    private getUnitConditionScore(unit: IUnit): string | undefined {
        if (unit.score) {
            return unit.score;
        }
        if (unit.derivedScore) {
            return unit.derivedScore;
        }
        if (unit.manifestations.length === 0) {
            return undefined;
        }
        return this.getConditionScoreFromChildren(unit.manifestations, false);
    }

    private getElementConditionScore(element: IElement): string | undefined {
        if (element.score && element.units.length === 0) {
            return element.score;
        }
        if (element.derivedScore) {
            return element.derivedScore;
        }
        if (element.units.length === 0) {
            return undefined;
        }
        return this.getConditionScoreFromChildren(element.units, false);
    }

    private getUnitCareScore(unit: IUnit): string | undefined {
        if (unit.careScore) {
            return unit.careScore;
        }
        if (unit.derivedCareScore) {
            return unit.derivedCareScore;
        }
        if (unit.manifestations.length === 0) {
            return undefined;
        }
        return this.getConditionScoreFromChildren(unit.manifestations, true);
    }

    private getElementCareScore(element: IElement): string | undefined {
        if (element.careScore) {
            return element.careScore;
        }
        if (element.derivedCareScore) {
            return element.derivedCareScore;
        }
        if (element.units.length === 0) {
            return undefined;
        }
        return this.getConditionScoreFromChildren(element.units, true);
    }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const ConditionReportNEN = translate()(ConditionReportNenComponent);
